'use memo'

import { useEffect, useState } from 'react'
// @ts-ignore Mixed JS module without declarations in this workspace.
import { SELECTED_PLAYER_ID } from '@sportninja/common/api/account'
import { ProfilePlayer } from '../types'

const STORAGE_KEY = SELECTED_PLAYER_ID

export interface UseSelectedPlayerResult {
  selectedPlayerId: string | null
  selectedPlayer: ProfilePlayer | null
  selectPlayer: (player?: ProfilePlayer | null) => void
}

export const useSelectedPlayer = (
  players: ProfilePlayer[],
  loadedPlayers: boolean
): UseSelectedPlayerResult => {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(
    () => {
      try {
        return window.localStorage.getItem(STORAGE_KEY)
      } catch {
        return null
      }
    }
  )

  const persistSelectedId = (id: string | null) => {
    try {
      if (id) {
        window.localStorage.setItem(STORAGE_KEY, id)
      } else {
        window.localStorage.removeItem(STORAGE_KEY)
      }
    } catch {
      // ignore storage failures (private mode, etc.)
    }
  }

  // Reconcile the persisted id against the fresh players list, but only once
  // the list has actually loaded — otherwise the initial empty list would wipe
  // a valid saved id before the fetch resolves. The saved id is honoured only
  // when it still exists in the list; otherwise we fall back to the first
  // player (or null when the list is empty) so downstream endpoints never fire
  // with a stale, no-longer-associated player id.
  useEffect(() => {
    if (!loadedPlayers) {
      return
    }

    const isSavedIdValid =
      selectedPlayerId != null &&
      players.some((p) => String(p.id) === selectedPlayerId)
    if (isSavedIdValid) {
      return
    }

    const nextId = players[0] ? String(players[0].id) : null
    if (nextId !== selectedPlayerId) {
      setSelectedPlayerId(nextId)
      persistSelectedId(nextId)
    }
  }, [players, selectedPlayerId, loadedPlayers])

  const selectedPlayer =
    players.find((p) => String(p.id) === selectedPlayerId) ?? players[0] ?? null

  const selectPlayer = (player?: ProfilePlayer | null) => {
    const id = player ? String(player.id) : null
    setSelectedPlayerId(id)
    persistSelectedId(id)
  }

  return { selectedPlayerId, selectedPlayer, selectPlayer }
}
